import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { EventType, AuthenticationResult } from '@azure/msal-browser'
import { initializeIcons } from '@fluentui/react'
import { msalInstance, silentRequest } from './config/authConfig'
import { ThemeProvider } from './context/ThemeContext'
import { DashboardPrefsProvider } from './context/DashboardPrefsContext'
import App from './App'
import '@fontsource/space-grotesk/700.css'
import './index.css'

initializeIcons()

// E2E test mode: bypass MSAL, render with a mock auth context.
// Dynamic import keeps the mock module out of the production bundle.
if (import.meta.env.VITE_E2E === 'true') {
  import('./e2e/renderE2EApp').then(({ renderE2EApp }) => renderE2EApp())
} else {
  bootApp()
}

function clearStuckInteractionState() {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('msal.') && (
      key.includes('.interaction.status') ||
      key.includes('.request.')
    )) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(key => {
    localStorage.removeItem(key)
  })
}

function renderApp() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ThemeProvider>
          <DashboardPrefsProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </DashboardPrefsProvider>
        </ThemeProvider>
      </MsalProvider>
    </React.StrictMode>,
  )
}

async function trySilentSso() {
  try {
    const result = await msalInstance.ssoSilent(silentRequest)
    if (result?.account) {
      msalInstance.setActiveAccount(result.account)
    }
  } catch {
    // Silent SSO commonly fails when third-party cookies are blocked or the user
    // has no active Microsoft session. Falling through renders the sign-in screen
    // which then redirects to the account picker.
  }
}

function bootApp() {
  // Keep the active account in sync only with events that represent a *user*
  // sign-in. Crucially, ACQUIRE_TOKEN_SUCCESS is NOT included here — that
  // event fires on every API token request, including silent token requests
  // for accounts other than the active one, and using it would race with
  // "Switch account" by snapping the active pointer back to whichever
  // account a background API call happened to use.
  msalInstance.addEventCallback(event => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS) &&
      (event.payload as AuthenticationResult)?.account
    ) {
      msalInstance.setActiveAccount((event.payload as AuthenticationResult).account)
    }
  })

  msalInstance.initialize().then(async () => {
    let redirectResult: AuthenticationResult | null = null
    try {
      redirectResult = await msalInstance.handleRedirectPromise()
    } catch (error) {
      console.error('Error handling redirect:', error)
      clearStuckInteractionState()
    }

    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account)
    } else if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      } else {
        await trySilentSso()
      }
    }

    renderApp()
  })
}
